if (!customElements.get('mh-quick-add')) {
  customElements.define(
    'mh-quick-add',
    class MHQuickAdd extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-mh-quick-dialog]');
        this.openButton = this.querySelector('[data-mh-quick-open]');
        this.closeButton = this.querySelector('[data-mh-quick-close]');
        this.variantSelect = this.querySelector('[data-mh-quick-variant]');
        this.optionSelects = Array.from(this.querySelectorAll('[data-mh-quick-option]'));
        this.image = this.querySelector('[data-mh-quick-image]');
        this.price = this.querySelector('[data-mh-quick-price]');
        this.badge = this.querySelector('[data-mh-quick-badge]');
        this.availability = this.querySelector('[data-mh-quick-availability]');
        this.quantityInput = this.querySelector('[data-mh-quick-quantity]');
        this.quantityMinus = this.querySelector('[data-mh-quick-quantity-minus]');
        this.quantityPlus = this.querySelector('[data-mh-quick-quantity-plus]');
        this.submitButton = this.querySelector('[data-mh-quick-submit]');
        // The open button is optional: mh-product-card-pt's Quick buy fetches this element and calls open() itself.
        if (!this.dialog || !this.variantSelect) return;

        this.openButton?.addEventListener('click', () => this.open());
        this.closeButton?.addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.dialog.close();
          }
        });
        this.dialog.addEventListener('close', () => {
          const target = this.returnFocus?.isConnected ? this.returnFocus : this.openButton;
          target?.focus();
          this.returnFocus = null;
        });
        this.optionSelects.forEach((select) => {
          select.addEventListener('change', () => this.selectOptionValue(select));
        });
        this.quantityMinus?.addEventListener('click', () => this.changeQuantity(-1));
        this.quantityPlus?.addEventListener('click', () => this.changeQuantity(1));
        this.quantityInput?.addEventListener('change', () => this.syncQuantity(false));
        this.quantityInput?.addEventListener('input', () => this.syncQuantityButtons());

        this.updateSelectedVariant({ resetQuantity: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (String(event.productVariantId) === String(this.variantSelect.value)) {
              this.dialog.close();
            }
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      open(returnFocus = null) {
        this.returnFocus = returnFocus;
        this.updateSelectedVariant({ resetQuantity: true });
        if (!this.dialog.open) this.dialog.showModal();
      }

      selectOptionValue(select) {
        const values = this.getSelectedValues();
        let variantOption = this.findVariantOption(values);

        if (!variantOption) {
          const position = Number(select.dataset.optionPosition) - 1;
          const desiredValue = select.value;
          const candidates = Array.from(this.variantSelect.options).filter(
            (option) => this.getVariantValues(option)[position] === desiredValue
          );
          variantOption = candidates.find((option) => option.dataset.available === 'true') || candidates[0];
        }

        if (!variantOption) return;
        this.variantSelect.value = variantOption.value;
        this.updateSelectedVariant({ resetQuantity: true });
      }

      updateSelectedVariant({ resetQuantity = false } = {}) {
        const variantOption = this.variantSelect.options[this.variantSelect.selectedIndex];
        if (!variantOption) return;

        const values = this.getVariantValues(variantOption);
        this.optionSelects.forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          if (values[position]) select.value = values[position];
        });

        const variantId = variantOption.value;
        const available = variantOption.dataset.available === 'true';
        this.replaceFromTemplate(this.price, 'price', variantId);
        this.replaceFromTemplate(this.badge, 'badge', variantId);
        this.replaceFromTemplate(this.availability, 'availability', variantId);

        if (this.availability) this.availability.dataset.available = String(available);
        if (this.submitButton) {
          this.submitButton.disabled = !available;
          const label = this.submitButton.querySelector('[data-mh-quick-submit-label]');
          if (label) label.textContent = available ? window.variantStrings.addToCart : window.variantStrings.unavailable;
        }

        if (this.image && variantOption.dataset.imageSrc) {
          this.image.src = variantOption.dataset.imageSrc;
          this.image.alt = variantOption.dataset.imageAlt || '';
          if (variantOption.dataset.imageWidth) this.image.width = Number(variantOption.dataset.imageWidth);
          if (variantOption.dataset.imageHeight) this.image.height = Number(variantOption.dataset.imageHeight);
        }

        this.syncColourSwatches();
        this.syncOptionAvailability();
        this.syncQuantity(resetQuantity);
      }

      getSelectedValues() {
        return this.optionSelects.map((select) => select.value);
      }

      getVariantValues(option) {
        return [1, 2, 3].map((position) => option.getAttribute(`data-option-${position}`) || '');
      }

      findVariantOption(values) {
        return Array.from(this.variantSelect.options).find((option) =>
          values.every((value, index) => !value || this.getVariantValues(option)[index] === value)
        );
      }

      syncOptionAvailability() {
        const variants = Array.from(this.variantSelect.options);
        this.optionSelects.forEach((select) => {
          const position = Number(select.dataset.optionPosition) - 1;
          Array.from(select.options).forEach((option) => {
            option.disabled = !variants.some(
              (variant) => variant.dataset.available === 'true' && this.getVariantValues(variant)[position] === option.value
            );
          });
        });
      }

      syncColourSwatches() {
        this.optionSelects.forEach((select) => {
          const swatch = select.closest('.mh-pcard__quick-select-wrap')?.querySelector('[data-mh-quick-selected-swatch]');
          if (!swatch) return;
          const colour = select.options[select.selectedIndex]?.dataset.mhColour || '';
          swatch.hidden = !colour;
          if (colour) swatch.style.setProperty('--mh-pcard-quick-swatch', colour);
        });
      }

      replaceFromTemplate(target, templateType, variantId) {
        const template = this.querySelector(
          `template[data-mh-quick-${templateType}-template][data-variant-id="${variantId}"]`
        );
        if (!target || !template) return;
        target.replaceChildren(template.content.cloneNode(true));
      }

      changeQuantity(direction) {
        if (!this.quantityInput) return;
        const step = Number(this.quantityInput.step) || 1;
        this.quantityInput.value = String((Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1) + direction * step);
        this.syncQuantity(false);
      }

      syncQuantity(reset) {
        if (!this.quantityInput) return;
        const variantOption = this.variantSelect.options[this.variantSelect.selectedIndex];
        if (!variantOption) return;

        const min = Number(variantOption.dataset.quantityMin) || 1;
        const step = Number(variantOption.dataset.quantityStep) || 1;
        const maxValue = Number(variantOption.dataset.quantityMax);
        const max = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : null;
        this.quantityInput.min = String(min);
        this.quantityInput.step = String(step);
        if (max) this.quantityInput.max = String(max);
        else this.quantityInput.removeAttribute('max');

        const requested = reset ? min : Number(this.quantityInput.value) || min;
        const stepped = min + Math.round((requested - min) / step) * step;
        this.quantityInput.value = String(Math.min(max || Number.POSITIVE_INFINITY, Math.max(min, stepped)));
        this.syncQuantityButtons();
      }

      syncQuantityButtons() {
        if (!this.quantityInput) return;
        const value = Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1;
        const min = Number(this.quantityInput.min) || 1;
        const maxValue = Number(this.quantityInput.max);
        this.quantityMinus?.toggleAttribute('disabled', value <= min);
        this.quantityPlus?.toggleAttribute('disabled', Number.isFinite(maxValue) && maxValue > 0 && value >= maxValue);
      }
    }
  );
}
